import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import CrudPage from "@/components/CrudPage";
import { individualsApi } from "@/lib/planApi";
import { housesApi } from "@/lib/orgApi";
import { UserCircle2 } from "lucide-react";

const IndividualsListPage = () => {
    const [houses, setHouses] = useState([]);
    useEffect(() => {
        housesApi
            .list()
            .then(setHouses)
            .catch(() => setHouses([]));
    }, []);

    return (
        <CrudPage
            title="Individuals"
            subtitle="People receiving services under one of your houses."
            api={individualsApi}
            testidPrefix="individuals"
            listParams={{ active: true }}
            columns={[
                {
                    key: "name",
                    label: "Name",
                    render: (r) => (
                        <Link
                            to={`/individuals/${r.id}`}
                            className="inline-flex items-center gap-2 font-semibold text-brand-blue-800 hover:text-brand-blue-950"
                            data-testid="individual-detail-link"
                        >
                            <UserCircle2 className="h-4 w-4" />
                            {r.first_name} {r.last_name}
                        </Link>
                    ),
                },
                {
                    key: "house_id",
                    label: "House",
                    render: (r) =>
                        houses.find((h) => h.id === r.house_id)?.name || "—",
                },
                { key: "disability_type", label: "Disability" },
                { key: "waiver_type", label: "Waiver" },
                {
                    key: "diagnoses",
                    label: "Diagnoses",
                    render: (r) =>
                        Array.isArray(r.diagnoses) && r.diagnoses.length
                            ? r.diagnoses.slice(0, 3).join(", ") +
                              (r.diagnoses.length > 3 ? "…" : "")
                            : "—",
                },
                {
                    key: "active",
                    label: "Status",
                    render: (r) =>
                        r.active ? (
                            <span className="rounded-full bg-brand-green-100 px-2.5 py-1 text-xs font-semibold text-brand-green-800">
                                Active
                            </span>
                        ) : (
                            <span className="rounded-full bg-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600">
                                Inactive
                            </span>
                        ),
                },
            ]}
            fields={[
                {
                    name: "house_id",
                    label: "House",
                    type: "select",
                    required: true,
                    options: houses.map((h) => ({
                        value: h.id,
                        label: h.name,
                    })),
                },
                { name: "first_name", label: "First name", required: true },
                { name: "last_name", label: "Last name", required: true },
                { name: "dob", label: "Date of birth", type: "date" },
                {
                    name: "disability_type",
                    label: "Disability type",
                    hint: "e.g. Intellectual Disability, Autism",
                },
                {
                    name: "disability_level",
                    label: "Disability level",
                    hint: "e.g. Mild, Moderate, Severe",
                },
                {
                    name: "waiver_type",
                    label: "Waiver",
                    type: "select",
                    options: [
                        { value: "BI", label: "BI — Building Independence" },
                        { value: "FIS", label: "FIS — Family & Individual Supports" },
                        { value: "CL", label: "CL — Community Living" },
                    ],
                },
                { name: "medicaid_id", label: "Medicaid ID" },
                {
                    name: "accountable_person",
                    label: "Accountable person",
                    hint: "e.g. legal guardian",
                },
                {
                    name: "active",
                    label: "Active",
                    type: "boolean",
                    default: true,
                },
            ]}
        />
    );
};

export default IndividualsListPage;
